// Recuadro de «Piezas pendientes»: piezas de cliente que el último pedido a ITSYS no ha podido atender (sin stock en
// ITSYS, poco stock o sin código ITSYS) y que siguen sin nada que las cubra. La cobertura es la de la propia página
// (stock → pedido a proveedor → pedido web); el motivo sale del pedido a ITSYS (backend/services/pedido_itsys).
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import api from '@/lib/api'

interface Pieza {
  key: string
  reference: string
  description: string
  id_order: number | null
  id: number | null
  origin: 'prestashop' | 'manual'
  customer: string | null
  date: string
  days_waiting: number
  uncovered_units: number
}

interface LineaPedido { codart: string; codigo: string | null; estado: string; pedir: number; descripcion: string | null }
interface DetallePedido { pedido: { id: number; estado: string; creado_at: string }; lineas: LineaPedido[] }

const MOTIVO: Record<string, string> = {
  sin_stock: 'ITSYS no tiene stock', stock_desconocido: 'no se pudo leer el stock de ITSYS',
  poco_stock: 'ITSYS no tiene stock suficiente', por_confirmar: 'falta el código de ITSYS',
}
const DIAS_VIGENTE = 7

export default function RosasSinAtenderItsys({ piezas }: { piezas: Pieza[] }) {
  const { data } = useQuery({
    queryKey: ['pedido-itsys-ultimo'],
    queryFn: async () => {
      const { data: l } = await api.get<{ pedidos: { id: number; estado: string; creado_at: string }[] }>('/pedido-itsys/pedidos')
      const u = (l.pedidos || []).find((p) => !['preparado', 'en_curso'].includes(p.estado))
      if (!u || Date.now() - new Date(u.creado_at).getTime() > DIAS_VIGENTE * 86400000) return null
      return (await api.get<DetallePedido>(`/pedido-itsys/pedidos/${u.id}`)).data
    },
    retry: false,
    staleTime: 60000,
  })
  if (!data) return null

  const noPedidas = new Map(data.lineas.filter((l) => MOTIVO[l.estado]).map((l) => [l.codart, l]))
  const porRef = new Map<string, { linea: LineaPedido; piezas: Pieza[] }>()
  for (const p of piezas) {
    const l = p.uncovered_units > 0 ? noPedidas.get(p.reference) : undefined
    if (!l) continue
    const g = porRef.get(p.reference) || { linea: l, piezas: [] }
    g.piezas.push(p)
    porRef.set(p.reference, g)
  }
  const grupos = [...porRef.values()].map((g) => ({ ...g, piezas: g.piezas.sort((a, b) => b.days_waiting - a.days_waiting) }))
    .sort((a, b) => b.piezas[0].days_waiting - a.piezas[0].days_waiting)
  if (!grupos.length) return null
  const n = grupos.reduce((s, g) => s + g.piezas.length, 0)
  const fecha = new Date(data.pedido.creado_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })

  return (
    <div className="rounded-xl border-2 border-pink-300 bg-pink-50 dark:border-pink-800 dark:bg-pink-950/30 p-4">
      <div className="font-semibold text-pink-800 dark:text-pink-200 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />
        {n} pieza{n !== 1 ? 's' : ''} de cliente sin atender en el último pedido a ITSYS (#{data.pedido.id}, {fecha})
      </div>
      <p className="text-xs text-pink-700/80 dark:text-pink-300/80 mt-0.5">
        No han entrado en el carrito de ITSYS y nada más las cubre. No se piden a otro proveedor: vuelven a salir en el próximo pedido.
      </p>
      <div className="mt-3 space-y-1.5">
        {grupos.map(({ linea: l, piezas: ps }) => (
          <div key={l.codart} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-pink-200 dark:border-pink-900 text-sm">
            <span className="font-mono font-semibold w-28">{l.codart}</span>
            <span className="flex-1 min-w-[160px] text-slate-600 dark:text-slate-300">{ps[0].description || l.descripcion}
              <span className="block text-xs text-pink-700 dark:text-pink-300">
                {MOTIVO[l.estado]}{l.pedir > 0 ? ` · se pidieron ${l.pedir}` : ''}{l.codigo ? ` · ITSYS ${l.codigo}` : ''}
              </span>
            </span>
            <span className="flex flex-wrap gap-1">
              {ps.map((p) => (
                <span key={p.key} title={`${p.customer || ''} · ${p.date}`}
                      className="px-2 py-0.5 rounded-md text-xs font-medium bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200">
                  {p.id_order ? `#${p.id_order}` : `a mano #${p.id}`} · {p.days_waiting} d · {p.uncovered_units} ud{p.uncovered_units !== 1 ? 's' : ''}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
